import {
  Alert, Anchor, Avatar, Badge, Box, Button, Card, Divider, Grid, Group, Progress, RingProgress,
  SimpleGrid, Skeleton, Stack, Table, Text, Title
} from "@mantine/core";
import {
  IconAddressBook, IconAlertTriangle, IconArrowLeft, IconBriefcase, IconBuilding,
  IconBuildingBank, IconListCheck, IconMapPin, IconUserOff, IconUserShield
} from "@tabler/icons-react";
import { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { CustomerInfoApi } from "../../../apis";
import type { CustomerContact } from "../../../apis/customerInfo";
import { EmptyState } from "../../../components/billing/EmptyState";
import { SectionHeader } from "../../../components/billing/SectionHeader";
import { PageLayout } from "../../../components/layout";

const heroWash = { background: "light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))" };

const completenessColor = (p: number): string => (p >= 80 ? "green" : p >= 50 ? "yellow" : "red");

const statusColor = (s?: string | null): string => {
  switch ((s ?? "").toUpperCase()) {
    case "ACTIVE": return "green";
    case "INVITED": return "blue";
    default: return "gray";
  }
};

const typeLabel = (type: string): string =>
  type === "BUYER" ? "Худалдан авагч" : type === "SUPPLIER" ? "Нийлүүлэгч" : type || "—";

const fieldValue = (v?: string | number | null): ReactNode => {
  if (v === null || v === undefined || (typeof v === "string" && v.trim() === "")) return <Text size="sm" c="dimmed">—</Text>;
  return <Text size="sm" fw={500}>{v}</Text>;
};

const Stat = ({ label, children }: { label: string; children: ReactNode }) => (
  <Stack gap={4}>
    <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>{label}</Text>
    {children}
  </Stack>
);

const Field = ({ label, value }: { label: string; value?: string | number | null | ReactNode }) => (
  <Box>
    <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.04em" }}>{label}</Text>
    {typeof value === "object" && value !== null && !Array.isArray(value) ? (value as ReactNode) : fieldValue(value as string | number | null)}
  </Box>
);

const ContactCard = ({ c }: { c: CustomerContact }) => (
  <Card withBorder radius="md" padding="sm">
    <Group justify="space-between" align="flex-start">
      <Box>
        <Group gap="xs">
          <Text size="sm" fw={600}>{c.name ?? "(нэргүй)"}</Text>
          {c.isPrimary && <Badge size="xs" color="brand" variant="light">Үндсэн</Badge>}
        </Group>
        {c.position && <Text size="xs" c="dimmed">{c.position}</Text>}
      </Box>
      <Stack gap={2} align="flex-end">
        {c.phone && <Text size="xs" style={{ fontVariantNumeric: "tabular-nums" }}>{c.phone}</Text>}
        {c.email && <Text size="xs" c="dimmed">{c.email}</Text>}
      </Stack>
    </Group>
  </Card>
);

const CustomerInfoDetail = () => {
  const navigate = useNavigate();
  const { partnerId } = useParams<{ partnerId: string }>();
  const { data, isLoading, error } = useSWR(
    partnerId ? `swr.my.customer-info.${partnerId}` : null,
    () => CustomerInfoApi.getCustomer(partnerId!)
  );

  return (
    <PageLayout
      title={data?.partner.businessName ?? "Харилцагч"}
      extra={[
        <Button
          key="back"
          variant="light"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/my-billing/customer-info")}
        >
          Буцах
        </Button>
      ]}
    >
      {isLoading ? (
        <Skeleton height={260} radius="md" />
      ) : error ? (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>Мэдээлэл ачааллахад алдаа гарлаа.</Alert>
      ) : !data ? (
        <Card withBorder radius="md" padding="md">
          <EmptyState icon={<IconUserOff size={26} />} message="Харилцагч олдсонгүй." />
        </Card>
      ) : (
        <Stack gap="md">
          {/* ── Hero ── */}
          <Card withBorder padding="lg" radius="md" style={heroWash}>
            <Group justify="space-between" align="center" wrap="nowrap" gap="lg">
              <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
                <Avatar size={64} radius="md" color="brand" variant="light">
                  {(data.partner.businessName ?? "?").charAt(0)}
                </Avatar>
                <Box style={{ minWidth: 0 }}>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.06em" }}>Харилцагч байгууллага</Text>
                  <Title order={3} lineClamp={1}>{data.partner.businessName ?? "(нэргүй)"}</Title>
                  <Group gap="xs" mt={2} wrap="nowrap" style={{ minWidth: 0 }}>
                    <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                      Регистр: {data.partner.regNumber ?? "—"}
                    </Text>
                    {data.partner.sectorName && (
                      <>
                        <Text size="sm" c="dimmed">·</Text>
                        <Text size="sm" c="dimmed" lineClamp={1}>{data.partner.sectorName}</Text>
                      </>
                    )}
                  </Group>
                  <Group gap="xs" mt="sm">
                    {data.businessProfile.businesses.map((b) => (
                      <Badge key={b.id} color={b.type === "BUYER" ? "brand" : "grape"} variant="light">{typeLabel(b.type)}</Badge>
                    ))}
                  </Group>
                </Box>
              </Group>

              <Group gap="xl" align="center" wrap="nowrap">
                <Group gap="xl" wrap="nowrap" visibleFrom="md">
                  <Stat label="Партнерийн төлөв">
                    <Badge color={statusColor(data.partner.partnerStatus)} variant="light" radius="sm">
                      {data.partner.partnerStatus ?? "—"}
                    </Badge>
                  </Stat>
                  <Stat label="Бүртгэл">
                    <Badge color={statusColor(data.partner.registerStatus)} variant="light" radius="sm">
                      {data.partner.registerStatus ?? "—"}
                    </Badge>
                  </Stat>
                </Group>
                <Box ta="center">
                  <RingProgress
                    size={92}
                    thickness={9}
                    roundCaps
                    sections={[{ value: data.completeness.overall.percent, color: completenessColor(data.completeness.overall.percent) }]}
                    label={
                      <Text ta="center" fw={700} size="md" style={{ fontVariantNumeric: "tabular-nums" }}>
                        {data.completeness.overall.percent}%
                      </Text>
                    }
                  />
                  <Text size="xs" c="dimmed" mt={2}>Мэдээллийн бүрдэл</Text>
                </Box>
              </Group>
            </Group>
          </Card>

          <Grid gap="md">
          <Grid.Col span={{ base: 12, lg: 8 }}>
          <Stack gap="md">

          {/* ── Байгууллагын мэдээлэл ── */}
          <Card withBorder padding="lg" radius="md">
            <Stack gap="md">
              <SectionHeader eyebrow="Үндсэн" title="Байгууллагын мэдээлэл" icon={<IconBuilding size={20} />} />
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                <Field label="Регистрийн дугаар" value={data.partner.regNumber} />
                <Field label="Нэр (МН)" value={data.partner.businessName} />
                <Field label="Нэр (EN)" value={data.partner.businessNameEng} />
                <Field label="Хуулийн этгээдийн төрөл" value={data.partner.legalEntityType} />
                <Field label="НӨАТ" value={
                  <Badge color={data.partner.isVatPayer ? "green" : "gray"} variant="light" size="sm">
                    {data.partner.isVatPayer ? "Төлөгч" : "Төлөгч бус"}
                  </Badge>
                } />
                <Field label="Үйл ажиллагааны чиглэл" value={data.partner.sectorName} />
                <Field label="Партнерийн төлөв" value={data.partner.partnerStatus} />
                <Field label="Бүртгэлийн төлөв" value={data.partner.registerStatus} />
                <Field label="Вэб" value={data.partner.web
                  ? <Anchor href={data.partner.web} target="_blank" size="sm">{data.partner.web}</Anchor>
                  : null} />
              </SimpleGrid>
            </Stack>
          </Card>

          {/* ── Хаяг байршил ── */}
          <Card withBorder padding="lg" radius="md">
            <Stack gap="md">
              <SectionHeader eyebrow="Байршил" title="Хаяг байршил" icon={<IconMapPin size={20} />} />
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                <Field label="Улс" value={data.address.country} />
                <Field label="Аймаг / хот" value={data.address.province} />
                <Field label="Дүүрэг" value={data.address.district} />
                <Field label="Хаяг" value={data.address.businessAddress} />
                <Field label="Координат" value={
                  data.address.locationLat != null && data.address.locationLng != null
                    && !(data.address.locationLat === 0 && data.address.locationLng === 0)
                    ? `${data.address.locationLat}, ${data.address.locationLng}`
                    : null
                } />
              </SimpleGrid>
            </Stack>
          </Card>

          {/* ── Холбоо барих ── */}
          <Card withBorder padding="lg" radius="md">
            <Stack gap="md">
              <SectionHeader eyebrow="Холбоо барих" title="Холбоо барих" icon={<IconAddressBook size={20} />} />
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Field label="И-мэйл" value={data.contact.email} />
                <Field label="Утас" value={data.contact.phone} />
              </SimpleGrid>
              <Divider label="Холбоо барих хүмүүс" labelPosition="left" />
              {data.contact.contacts.length === 0 ? (
                <Text size="sm" c="dimmed">Холбоо барих хүн бүртгэгдээгүй.</Text>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  {data.contact.contacts.map((c) => <ContactCard key={c.id} c={c} />)}
                </SimpleGrid>
              )}
            </Stack>
          </Card>

          {/* ── Бизнес профайл ── */}
          <Card withBorder padding="lg" radius="md">
            <Stack gap="md">
              <SectionHeader eyebrow="Профайл" title="Бизнес профайл" icon={<IconBriefcase size={20} />} />
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                <Field label="Худалдан авагч бизнес" value={data.businessProfile.buyerBusinessName} />
                <Field label="Нийлүүлэгч бизнес" value={data.businessProfile.supplierBusinessName} />
                <Field label="Бизнесийн төлөв" value={
                  data.businessProfile.businessStatus
                    ? <Badge color={data.businessProfile.businessStatus === "ACTIVE" ? "green" : "gray"} variant="light" size="sm">
                        {data.businessProfile.businessStatus}
                      </Badge>
                    : null
                } />
              </SimpleGrid>
            </Stack>
          </Card>

          {/* ── Банкны данс ── */}
          <Card withBorder padding="lg" radius="md">
            <Stack gap="md">
              <SectionHeader eyebrow="Санхүү" title="Банкны данс" icon={<IconBuildingBank size={20} />} />
              {data.bankAccounts.length === 0 ? (
                <Text size="sm" c="dimmed">Банкны данс бүртгэгдээгүй.</Text>
              ) : (
                <Table.ScrollContainer minWidth={560}>
                  <Table verticalSpacing="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Банк</Table.Th>
                        <Table.Th>Дансны дугаар</Table.Th>
                        <Table.Th>Эзэмшигч</Table.Th>
                        <Table.Th>Валют</Table.Th>
                        <Table.Th>Төлөв</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data.bankAccounts.map((a) => (
                        <Table.Tr key={a.id}>
                          <Table.Td><Text size="sm" fw={500}>{a.bankName ?? "—"}</Text></Table.Td>
                          <Table.Td><Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{a.number ?? "—"}</Text></Table.Td>
                          <Table.Td><Text size="sm">{a.name ?? "—"}</Text></Table.Td>
                          <Table.Td><Text size="sm">{a.currency ?? "—"}</Text></Table.Td>
                          <Table.Td>
                            <Group gap={6}>
                              {a.isDefault && <Badge size="xs" color="brand" variant="light">Үндсэн</Badge>}
                              {a.isVerified && <Badge size="xs" color="green" variant="light">Баталгаажсан</Badge>}
                              {!a.isDefault && !a.isVerified && <Text size="xs" c="dimmed">—</Text>}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
            </Stack>
          </Card>

          {/* ── Байгууллагын админ ── */}
          <Card withBorder padding="lg" radius="md">
            <Stack gap="md">
              <SectionHeader eyebrow="Админ" title="Байгууллагын админ" icon={<IconUserShield size={20} />} />
              {!data.admin ? (
                <Text size="sm" c="dimmed">Байгууллагын админы мэдээлэл алга.</Text>
              ) : (
                <Stack gap="md">
                  <Group gap="md" align="flex-start" wrap="nowrap">
                    <Avatar src={data.admin.avatar ?? undefined} radius="xl" size="lg" color="brand">
                      {(data.admin.name ?? "?").charAt(0)}
                    </Avatar>
                    <Box style={{ flex: 1 }}>
                      <Group gap="xs" align="center">
                        <Text size="sm" fw={600}>{data.admin.name ?? "(нэргүй)"}</Text>
                        {data.admin.isAdmin && <Badge size="xs" color="brand" variant="light">Админ эрхтэй</Badge>}
                        {data.admin.userStatus && (
                          <Badge size="xs" variant="light" color={data.admin.userStatus === "ACTIVE" ? "green" : "gray"}>
                            {data.admin.userStatus}
                          </Badge>
                        )}
                      </Group>
                      <Group gap="xs" mt={6}>
                        <Badge size="xs" variant="light" color={data.admin.isEmailVerified ? "green" : "gray"}>
                          И-мэйл {data.admin.isEmailVerified ? "баталгаажсан" : "баталгаажаагүй"}
                        </Badge>
                        <Badge size="xs" variant="light" color={data.admin.isPhoneVerified ? "green" : "gray"}>
                          Утас {data.admin.isPhoneVerified ? "баталгаажсан" : "баталгаажаагүй"}
                        </Badge>
                        <Badge size="xs" variant="light" color={data.admin.isDanVerified ? "green" : "gray"}>
                          ДАН {data.admin.isDanVerified ? "баталгаажсан" : "баталгаажаагүй"}
                        </Badge>
                      </Group>
                    </Box>
                  </Group>
                  <Divider />
                  <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                    <Field label="Овог" value={data.admin.familyName} />
                    <Field label="Регистрийн дугаар" value={data.admin.registerNo} />
                    <Field label="Нэвтрэх нэр" value={data.admin.username} />
                    <Field label="И-мэйл" value={data.admin.email} />
                    <Field label="И-мэйл 2" value={data.admin.email2} />
                    <Field label="Утас" value={data.admin.phone} />
                    <Field label="Утас 2" value={data.admin.phone2} />
                    <Field label="Бүртгүүлсэн" value={data.admin.createdAt ? data.admin.createdAt.slice(0, 10) : null} />
                  </SimpleGrid>
                </Stack>
              )}
            </Stack>
          </Card>

          </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Box style={{ position: "sticky", top: 76 }}>
              {/* ── Бөглөлтийн байдал ── */}
              <Card withBorder padding="lg" radius="md">
                <Stack gap="md">
                  <SectionHeader eyebrow="Бүрдэл" title="Бөглөлтийн байдал" icon={<IconListCheck size={20} />} />
                  <Box>
                    <Group justify="space-between" mb={6}>
                      <Text size="sm" c="dimmed">Нийт бөглөлт</Text>
                      <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                        {data.completeness.overall.filled} / {data.completeness.overall.total}
                      </Text>
                    </Group>
                    <Progress
                      value={data.completeness.overall.percent}
                      color={completenessColor(data.completeness.overall.percent)}
                      radius="xl"
                      size="md"
                    />
                  </Box>
                  <Divider />
                  <Stack gap="sm">
                    {data.completeness.sections.map((s) => {
                      const pct = s.total === 0 ? 0 : Math.round((s.filled / s.total) * 100);
                      return (
                        <Box key={s.key}>
                          <Group justify="space-between" mb={4}>
                            <Text size="sm">{s.label}</Text>
                            <Text size="xs" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                              {s.filled} / {s.total}
                            </Text>
                          </Group>
                          <Progress value={pct} color={completenessColor(pct)} radius="xl" size="sm" />
                        </Box>
                      );
                    })}
                  </Stack>
                </Stack>
              </Card>
            </Box>
          </Grid.Col>
          </Grid>
        </Stack>
      )}
    </PageLayout>
  );
};

export default CustomerInfoDetail;
